import {
  createStyles,
  Grid,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import { colors } from 'design-system';

import {
  CoverageCatalog,
  QuotationCoverage,
  QuotationUnit,
} from 'data-layer-cr/dist/models';

import CatalogItem from 'src/models/helperTypes/CatalogItem';
import PricingProfileResponse from 'src/models/responseTypes/PricingProfileResponse';

// Components
import { faSparkles } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { SelectionCard } from 'src/components/General';
import { AssaMobile } from '../../../components';
import FreeMonthCard from '../../../components/FreeMonthCard';

export const platinoPlusDescription = '(Exoneración del deducible)';
export const platinoPlusName = `ENDOSO PLATINO PLUS`;

type Props = {
  onChange: (
    modifiedPackage: number,
    newCode: string,
    newValue: number,
  ) => void;
  value: string;
  currentProfile: PricingProfileResponse;
  selectedPackage: number;
  additionalBenefits?: CoverageCatalog[];
  unit: QuotationUnit;
  setCurrentEndorsement: (newCode: string) => void;
  options: CatalogItem<CoverageCatalog>[];
  currentSelectedPlan: string;
  onChangeLoadingPackage?: () => void;
  typeOfMoney: string;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    closeIcon: {
      position: 'absolute',
      top: 14,
      right: theme.spacing(2),
      zIndex: 1101,
      [theme.breakpoints.down('sm')]: {
        position: 'fixed',
      },
    },
    dialogue: {
      borderRadius: 10,
      [theme.breakpoints.down('md')]: {
        borderRadius: 0,
      },
    },
    titleText: {
      color: colors.white,
      [theme.breakpoints.down('sm')]: {
        color: colors.madison,
      },
    },
    heartImage: {
      marginRight: 12,
      marginBottom: 15,
    },
    additionalBenefitsText: {
      fontSize: 19,
      fontWeight: 900,
    },
    extraBenefitsText: {
      marginTop: 15,
      marginBottom: 7,
      fontSize: 17,
      fontWeight: 400,
    },
    optionContainer: ({ selectedPackage }: { selectedPackage: number }) => ({
      width: selectedPackage !== 2 ? '100%' : 'calc(100% - 35px)',
    }),
    coverageText: ({ selectedPackage }: { selectedPackage: number }) => ({
      fontSize: 17,
      fontWeight: 700,
      marginTop: selectedPackage !== 2 ? '0.5rem' : 0,
    }),
    coverageTextDisabled: { color: colors.light_silver },
    platinoMessage: { fontSize: 12, fontWeight: 700 },
    platinoBox: {
      width: '100%',
      marginTop: 15,
      height: 40,
      backgroundColor: '#F5FAFF',
      border: '1px solid #DADADA',
      borderRadius: 10,
      display: 'flex',
      alignItems: 'center',
    },
    coverageValue: ({ selectedPackage }: { selectedPackage: number }) => ({
      fontSize: 19,
      fontWeight: 700,
      marginTop: selectedPackage !== 2 ? '0.5rem' : 0,
    }),
  }),
);

// Sorts by prices
export function sortByPrice(coverages: CoverageCatalog[]): CoverageCatalog[] {
  return coverages.sort((a, b) => b.maxCost - a.maxCost);
}

// Remove the platinum Plus endorse
export function filterPlatinumPLus(
  coverages: CoverageCatalog[],
): CoverageCatalog[] {
  return coverages.filter((coverage) => coverage.name !== platinoPlusName);
}

const selectCoverage = (
  unit?: QuotationUnit,
  coverageId?: string,
): QuotationCoverage | undefined => {
  return unit?.coverages.find(
    (coverage: QuotationCoverage): boolean =>
      coverage.coverageId === coverageId,
  );
};

const DynamicEndorsementCard = ({
  onChange,
  currentProfile,
  selectedPackage,
  additionalBenefits = [],
  unit,
  setCurrentEndorsement,
  options,
  currentSelectedPlan,
  onChangeLoadingPackage,
  typeOfMoney,
}: Props): JSX.Element => {
  const classes = useStyles({ selectedPackage });
  const [endorsement, setEndorsement] = useState(
    selectCoverage(unit, '0049') || selectCoverage(unit, '0040'),
  );

  useEffect(() => {
    setEndorsement(
      selectCoverage(unit, '0049') || selectCoverage(unit, '0040'),
    );
  }, [unit]);

  function onUpdateCurrentOption(option: CatalogItem): void {
    onChangeLoadingPackage?.();
    if (option.Object) {
      setCurrentEndorsement(option.Value);
      onChange(
        selectedPackage,
        option.Value,
        (option.Object as CoverageCatalog).maxCost,
      );
    } else {
      setCurrentEndorsement(option.Value);
      onChange(selectedPackage, option.Value, 0);
    }
  }

  return (
    <SelectionCard
      inputId="sales-web-button-additionalbenefits"
      title={
        <Grid container data-testid="additional-benefits-title">
          <Grid item container style={{ gap: '0.5rem' }}>
            <FontAwesomeIcon icon={faSparkles} size="2xl" color="#0D2E68" />
            <Typography className={classes.additionalBenefitsText}>
              Beneficios de la póliza
            </Typography>
          </Grid>
          <AssaMobile />
          {['RetencionBacCRC', 'RetencionBacUSD'].includes(
            currentProfile.productId,
          ) && <FreeMonthCard />}
          {additionalBenefits.map((ab) => (
            <Grid item container key={ab.code}>
              <Typography className={classes.extraBenefitsText}>
                {ab.name}
              </Typography>
            </Grid>
          ))}
        </Grid>
      }
      endorsement={endorsement}
      options={options}
      onUpdateCurrentOption={onUpdateCurrentOption}
      currentSelectedPlan={currentSelectedPlan}
      typeOfMoney={typeOfMoney}
    />
  );
};

export default DynamicEndorsementCard;
